/**
 * @fileoverview Document processing failure classes (MOR-528)
 *
 * Purpose: A single, framework-agnostic taxonomy that turns the free-text error
 * strings produced by the extraction pipeline into a small set of distinct
 * failure classes, each with practitioner-facing copy. Used on both sides:
 *   - Backend: failure mutations stamp `*FailureClass` on the document row via
 *     `classifyFailure(errorMessage)`.
 *   - Frontend: `ProcessingStatusBadge` reads `FAILURE_CLASS_META` to render the
 *     pill label and the hover tooltip explaining *why* a document failed.
 *
 * Keep this file dependency-free (no `convex/*`, no Node built-ins) so it can be
 * imported from Convex queries/mutations AND React components alike.
 */

/** Distinct failure classes surfaced in the UI. */
export type FailureClass =
  | "cost_limit"
  | "password_protected"
  | "corrupt_unreadable"
  | "file_too_large"
  | "unsupported_format"
  | "extraction_timeout"
  | "service_error"
  | "unknown";

export interface FailureClassMeta {
  /** Short label shown on the status pill. */
  label: string;
  /** One-line explanation shown in the hover tooltip. */
  hint: string;
  /** Whether retrying could plausibly succeed (drives the tooltip note). */
  retriable: boolean;
}

/**
 * Display copy per class. Kept terse and practitioner-facing — no jargon, no
 * em dashes. The tooltip composes `hint` with the specific error message.
 */
export const FAILURE_CLASS_META: Record<FailureClass, FailureClassMeta> = {
  cost_limit: {
    label: "Too large to process",
    hint: "This document is too large to process right now. Try splitting it into smaller files and re-uploading.",
    retriable: false,
  },
  password_protected: {
    label: "Password protected",
    hint: "The file is password protected. Remove the password and re-upload it.",
    retriable: false,
  },
  corrupt_unreadable: {
    label: "Unreadable file",
    hint: "The file appears corrupt or could not be read. Try re-exporting or re-uploading it.",
    retriable: false,
  },
  file_too_large: {
    label: "File too large",
    hint: "The file exceeds the maximum size for extraction. Split it into smaller files and re-upload.",
    retriable: false,
  },
  unsupported_format: {
    label: "Unsupported format",
    hint: "This file type cannot be processed. Convert it to PDF, DOCX, or XLSX and re-upload.",
    retriable: false,
  },
  extraction_timeout: {
    label: "Timed out",
    hint: "Processing took too long, usually because the file is very large or complex. You can retry it.",
    retriable: true,
  },
  service_error: {
    label: "Service error",
    hint: "The extraction service had a temporary problem. You can retry it.",
    retriable: true,
  },
  unknown: {
    label: "Failed",
    hint: "Processing failed for an unknown reason. You can retry it, or re-upload the file.",
    retriable: true,
  },
};

/**
 * Maps a raw error message to a {@link FailureClass}. The patterns match the
 * strings produced by `mapReductoError` and the cost-guard throw in
 * `extraction/actions.ts`. Order matters: the most specific checks come first
 * (e.g. the cost-limit message also contains "too large", so it is matched
 * before the file-size check).
 */
export function classifyFailure(
  errorMessage: string | undefined | null,
): FailureClass {
  if (!errorMessage) return "unknown";
  const msg = errorMessage.toLowerCase();

  // Cost guard (MOR-528) — must come before file_too_large.
  if (
    msg.includes("exceeds processing cost limit") ||
    msg.includes("cost limit")
  ) {
    return "cost_limit";
  }

  if (
    msg.includes("password-protected") ||
    msg.includes("password protected") ||
    msg.includes("document_password_protected") ||
    msg.includes("password")
  ) {
    return "password_protected";
  }

  if (
    msg.includes("corrupt or unreadable") ||
    msg.includes("document_corrupt") ||
    msg.includes("cannot be parsed") ||
    msg.includes("damaged")
  ) {
    return "corrupt_unreadable";
  }

  if (
    msg.includes("file too large") ||
    msg.includes("exceeds the maximum size") ||
    msg.includes("exceeds maximum document size") ||
    msg.includes("413")
  ) {
    return "file_too_large";
  }

  if (
    msg.includes("unsupported format") ||
    msg.includes("appears to be binary") ||
    msg.includes("binary") ||
    msg.includes("415")
  ) {
    return "unsupported_format";
  }

  if (msg.includes("timed out") || msg.includes("timeout")) {
    return "extraction_timeout";
  }

  if (
    msg.includes("authentication failed") ||
    msg.includes("auth_error") ||
    msg.includes("401") ||
    msg.includes("rate_limit") ||
    msg.includes("rate limit") ||
    msg.includes("429") ||
    msg.includes("service")
  ) {
    return "service_error";
  }

  return "unknown";
}
